/*
  hf-film.js: the illustrated Home film. One scroll timeline drives the scenes ("shots") on a
  pinned stage. Vanilla, no dependencies, no build step. assets/hf-film.css pins the stage and lays
  out the still version; each scene's look lives in its block (blocks/hf-film-*.liquid) and its
  scene CSS file. Scene builders never need to read below this comment.

  TIME
    Film time t is counted in screens: one screen is one stage height of scrolling. Shot k runs
    from S to S + L. It starts `overlap` screens before the shot above it ends and fades in on top
    of it over that overlap. Inside a shot p runs 0..1, and every timing below is in p of its own
    shot. The stage stays pinned until the last shot reaches p 1, then scrolls away with it.

  MARKUP
    [data-film]                   Section root. Live it carries .is-film-live, .is-film-loaded (after
                                  window load), data-film-orient="l|p", data-film-current (shot name)
                                  and --film-aw / --film-ah (the art box in px).
      [data-film-track]           Wraps the stage. The engine sets its height.
        [data-film-stage]         Sticky, one screen tall, under the header (--hf-header-h).
          [data-film-shot="name"] One per scene, in order. Gets .is-on while showing, --film-p, and
                                  --film-ground from data-film-ground.
              data-film-len       L in screens, landscape. Default 1.
              data-film-len-p     L in portrait. Default data-film-len.
              data-film-overlap   Screens shared with the shot before, crossfaded. Default 0.
              data-film-in="cut"  No crossfade: your keyframes do the handover inside the overlap.
              data-film-ground    The colour behind the art, e.g. "#E5EA98".
              data-film-hold      The p the scene's still matches, with every cue showing. The theme
                                  editor and keyboard focus seek here. Default 0.5.
            script[type="application/json"][data-film-keys]   The shot's keyframes (KEYFRAMES).
            [data-film-art]       The art box: 16:9 in landscape, 9:16 in portrait (the stage is
                                  taller than wide), cover fit and centred, so up to about 12% of an
                                  edge can be cropped on odd screens. Keep essentials inside the
                                  middle 76% of each axis. Mark it aria-hidden="true".
              [data-film-cam]     The camera. Its "cam" keys move every layer inside it, times the
                                  layer's depth. Non-layer elements inside it stay put.
                [data-film-layer="name"]  A plane or sprite. Named layers take keys under that name.
                                  Default place: the whole art box (zero specificity, so any class of
                                  yours wins). Give layer images width and height. Do not nest layers.
                    data-film-depth   How far the camera moves it: 0 fixed (sky), 1 default, over 1
                                      for the foreground.
                    data-film-pivot   "x y" in % of the art box: the point it turns and scales about.
                                      Default its own centre; the cam's default is the art centre.
              A layer outside the art (copy that slides) works in % of the stage, without the camera.
            [data-film-flip]      Hard cuts between its [data-film-at="p"] children, in order: the
                                  last one whose p has passed shows (data-film-on), the first before.
            canvas[data-film-clip="a,b"]  A frame sequence scrubbed from p a to p b; contain fit, or
                                  data-film-fit="cover". Its next sibling is script[data-film-frames]
                                  JSON {"l": [urls], "p": [urls]} (render 'hf-film-frames'). Frame i
                                  of F shows at a + (b - a) * i / (F - 1). Add data-film-layer to move it.
            [data-film-cue="a"] or "a,b"   Text reveal: .is-cued while a <= p < b; CSS animates it.
                                  Never on a layer (both write transform).
            [data-film-draw="a,b"]  A stroke draw from p a to b: SVG shapes inside draw in document
                                  order, and --film-draw 0..1 is set for anything else (the scribble
                                  note wipes on it). render 'hf-film-scribble' does both.
            [data-film-tally="a,b"] Counts the number in its text up from data-film-from (default 0)
                                  between a and b. Give it its own element: it holds its final width.
            picture[data-film-still]  The scene still for reduced motion and no JS (render
                                  'hf-film-still'). Its sources match only under reduced motion.
            Images in the shot load late: data-film-src / data-film-srcset instead of src / srcset,
            on the img and on any <source>. The engine sets them when the shot enters the load window.
            .hf-film__copy        The scene's words, outside [data-film-art]. hf-film.css keeps the
                                  bottom 88px plus the safe area clear for the sticky buy bar.

  KEYFRAMES  One JSON object per shot, landscape "l" and portrait "p", keyed by layer name:
      {"l": {"cam":     {"0": {"y": 0}, "0.6": {"y": -40, "e": "inOut"}},
             "coconut": {"0.2": {"y": 0, "r": 0}, "0.7": {"y": 55, "r": 190, "e": "in"}}},
       "p": {"coconut": {"0.2": {"y": 0}, "0.8": {"y": 70, "e": "in"}}}}
    A name missing from "p" uses its "l" keys. Keys are p within the shot, 0..1.
    x, y   move, in % of the art box's width and height     s  scale (1)
    r      turn, degrees clockwise                           o  opacity 0..1 (1)
    Each property eases between the keys that name it and holds before the first and after the last.
    "e" on a key is how the value arrives there: "linear" (default), "in", "out", "inOut" (cubic),
    "brand" (the site's --hf-ease), "hold" (jumps at the key), or [x1, y1, x2, y2] as cubic-bezier().
    The cam's x, y and r move a layer times its depth, its s scales it by 1 + (s - 1) * depth, about
    the cam's pivot; its o fades the whole cam. The engine writes transform (a matrix about 0 0)
    and opacity inline on layers: style layers with classes, never animate those two from CSS.

  BUDGETS  The first shot's art 300 KB at most (it is all that loads before the first scroll).
    Frames no bigger than they draw. The engine holds at most 24 decoded frames per clip.

  LOADING  Nothing before window load. Then the shot on screen only, until the reader scrolls.
    Then from one shot behind to two ahead. A clip loads a sparse set first, so every position has
    a near frame, then the frames round the playhead in the direction of travel; a fast fling
    (skim) loads only the sparse set. A canvas never clears without drawing: a frame in flight
    shows the nearest loaded one. Same page links that jump across the film jump instantly.

  REDUCED MOTION AND NO JS  Nothing mounts: each shot is its still, then all its words. Nothing
    pins and no film art or frame is requested.

  THEME EDITOR  Selecting a scene's block seeks to its hold; section load and unload remount.

  VERIFY  window.HfFilm: seek(t) or seek(name, p) (instant, returns t), state(), sample(el, p)
    (layer or cam values and the matrix it gets) or sample(keys, p) (one layer's keys), scan(scope),
    drop(scope). Add ?film-debug to the URL for a readout, layer outlines and the buy bar zone.
*/
(function () {
  'use strict';
  if (window.HfFilm) return;

  var reduceMQ = matchMedia('(prefers-reduced-motion: reduce)');
  var MAX_LOADS = 4;   // frame requests in flight per film
  var MAX_BMP = 24;    // decoded frames held per clip
  var SPARSE = 8;      // about this many frames spread through a clip load first
  var SKIM = 4;        // screens a second; faster is a fling
  var GEO = 'path,line,polyline,polygon,circle,ellipse,rect';
  var PROPS = { x: 0, y: 0, s: 1, r: 0, o: 1 };
  var RAD = Math.PI / 180;
  var live = [];
  var pageLoaded = document.readyState === 'complete';
  var debug = /[?&]film-debug\b/.test(location.search);

  function clamp01(x) { return x < 0 ? 0 : x > 1 ? 1 : x; }
  function all(scope, sel) { return [].slice.call(scope.querySelectorAll(sel)); }
  function num(v, d) { v = parseFloat(v); return isNaN(v) ? d : v; }
  function range(v, b) { v = (v || '').split(','); return [num(v[0], 0), num(v[1], b)]; }
  function seg(p, r) { return r[1] > r[0] ? clamp01((p - r[0]) / (r[1] - r[0])) : p >= r[0] ? 1 : 0; }
  function r4(x) { return Math.round(x * 1e4) / 1e4; }

  // ---- easing and keys ------------------------------------------------------------------------
  function cub(u, a, b) { return ((1 - 3 * b + 3 * a) * u + 3 * b - 6 * a) * u * u + 3 * a * u; }
  function bezier(x1, y1, x2, y2) {
    return function (t) {
      var lo = 0, hi = 1, u = t;
      for (var i = 0; i < 24; i++) {
        if (cub(u, x1, x2) < t) lo = u; else hi = u;
        u = (lo + hi) / 2;
      }
      return cub(u, y1, y2);
    };
  }
  var EASE = {
    linear: function (t) { return t; },
    'in': function (t) { return t * t * t; },
    out: function (t) { return 1 - Math.pow(1 - t, 3); },
    inOut: function (t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(2 - 2 * t, 3) / 2; },
    brand: bezier(0.2, 0.7, 0.2, 1),
    hold: function (t) { return t < 1 ? 0 : 1; }
  };
  function ease(e) {
    if (e && e.length === 4 && typeof e !== 'string') return bezier(e[0], e[1], e[2], e[3]);
    return EASE[e] || EASE.linear;
  }

  // {"0": {"y": 0}, "0.6": {"y": -40, "e": "in"}} -> {y: [[0, 0, fn], [0.6, -40, fn]]}
  function track(keys) {
    var out = {};
    if (!keys) return out;
    Object.keys(keys).map(function (k) { return [parseFloat(k), keys[k]]; })
      .sort(function (a, b) { return a[0] - b[0]; })
      .forEach(function (kv) {
        for (var n in PROPS) if (n in kv[1]) (out[n] = out[n] || []).push([kv[0], +kv[1][n], ease(kv[1].e)]);
      });
    return out;
  }
  function at(list, p, d) {
    if (!list) return d;
    var n = list.length, i = 0;
    if (p <= list[0][0]) return list[0][1];
    if (p >= list[n - 1][0]) return list[n - 1][1];
    while (p > list[i + 1][0]) i++;
    var a = list[i], b = list[i + 1];
    return a[1] + (b[1] - a[1]) * b[2]((p - a[0]) / (b[0] - a[0]));
  }
  function sampleTrack(tr, p) {
    var v = {};
    for (var n in PROPS) v[n] = at(tr[n], p, PROPS[n]);
    return v;
  }
  function tracks(keys, name) {
    var l = track(keys.l && keys.l[name]);
    return { l: l, p: keys.p && keys.p[name] ? track(keys.p[name]) : l };
  }

  // 2D affine matrices as CSS matrix(a, b, c, d, e, f).
  function mul(m, n) {
    return [m[0] * n[0] + m[2] * n[1], m[1] * n[0] + m[3] * n[1], m[0] * n[2] + m[2] * n[3],
      m[1] * n[2] + m[3] * n[3], m[0] * n[4] + m[2] * n[5] + m[4], m[1] * n[4] + m[3] * n[5] + m[5]];
  }
  // Move by (tx, ty), then turn r degrees and scale s about the point (px, py).
  function tf(tx, ty, s, r, px, py) {
    var c = Math.cos(r * RAD) * s, n = Math.sin(r * RAD) * s;
    return [c, n, -n, c, px + tx - c * px + n * py, py + ty - n * px - c * py];
  }

  function format(c, v) {
    var s = v.toFixed(c.dec);
    if (c.comma) s = s.replace(/^(-?\d+)/, function (w) { return w.replace(/\B(?=(\d{3})+$)/g, ','); });
    return c.pre + s + c.post;
  }

  // The page's html sets scroll-behavior: smooth, which would scrub the whole film on a jump.
  function instant(fn) {
    var st = document.documentElement.style, was = st.scrollBehavior;
    st.scrollBehavior = 'auto';
    fn();
    st.scrollBehavior = was;
  }

  function mount(root) {
    if (root.__hfFilm) return;
    var trackEl = root.querySelector('[data-film-track]');
    var stage = root.querySelector('[data-film-stage]');
    var shotEls = all(root, '[data-film-shot]');
    if (!trackEl || !stage || !shotEls.length) return;

    var ac = new AbortController();
    var opt = { passive: true, signal: ac.signal };
    var dead = false, raf = 0, layoutRaf = 0, settle = 0;
    var W = 0, H = 0, top = 0, orient = '', T = 0, t = 0, cur = -1, aw = 0, ah = 0;
    var engaged = false, near = false, skim = false, lastT = -1, lastNow = 0, dirn = 1, loads = 0;
    var queue = [], dbg = null;

    function schedule() { if (!raf && !dead) raf = requestAnimationFrame(update); }
    function scheduleLayout() { if (!layoutRaf && !dead) layoutRaf = requestAnimationFrame(layout); }

    var shots = shotEls.map(function (el, i) {
      var keys = {}, ks = el.querySelector('script[data-film-keys]');
      if (ks) {
        try { keys = JSON.parse(ks.textContent); } catch (e) { console.warn('hf-film: keys are not JSON in shot', i, e); }
      }
      var camEl = el.querySelector('[data-film-cam]');
      function pivot(e, d) {
        var v = (e.getAttribute('data-film-pivot') || '').split(/[\s,]+/);
        return v.length > 1 ? [num(v[0], 50), num(v[1], 50)] : d;
      }
      var s = {
        el: el, i: i, name: el.getAttribute('data-film-shot') || String(i),
        lenL: num(el.getAttribute('data-film-len'), 1), lenP: num(el.getAttribute('data-film-len-p'), NaN),
        over: num(el.getAttribute('data-film-overlap'), 0), fade: el.getAttribute('data-film-in') !== 'cut',
        hold: num(el.getAttribute('data-film-hold'), 0.5), S: 0, L: 1, O: 0, p: -1, on: false, op: '',
        promoted: false, imgs: all(el, '[data-film-src],[data-film-srcset]'),
        cam: camEl ? { el: camEl, tr: tracks(keys, 'cam'), pivot: pivot(camEl, [50, 50]), o: '' } : null
      };
      s.layers = all(el, '[data-film-layer]').map(function (le) {
        var art = le.closest('[data-film-art]');
        return {
          el: le, tr: tracks(keys, le.getAttribute('data-film-layer')), depth: num(le.getAttribute('data-film-depth'), 1),
          pivot: pivot(le, null), ref: art || stage, cam: !!(camEl && art && camEl.contains(le)), box: [0, 0, 0, 0], m: '', o: ''
        };
      });
      s.cues = all(el, '[data-film-cue]').map(function (c) { return { el: c, r: range(c.getAttribute('data-film-cue'), Infinity), on: null }; });
      s.draws = all(el, '[data-film-draw]').map(function (d) {
        return { el: d, r: range(d.getAttribute('data-film-draw'), 1), v: '', total: 0,
          segs: (d.matches(GEO) ? [d] : all(d, GEO)).map(function (g) { return { el: g, len: 0 }; }) };
      });
      // The rendered number is the target.
      s.tallies = all(el, '[data-film-tally]').map(function (e) {
        var text = e.textContent, m = text.match(/-?[\d,]*\.?\d+/);
        return m && {
          el: e, text: text, pre: text.slice(0, m.index), post: text.slice(m.index + m[0].length),
          to: parseFloat(m[0].replace(/,/g, '')), dec: (m[0].split('.')[1] || '').length, comma: m[0].indexOf(',') > -1,
          from: num(e.getAttribute('data-film-from'), 0), r: range(e.getAttribute('data-film-tally'), 1), v: null
        };
      }).filter(Boolean);
      s.flips = all(el, '[data-film-flip]').map(function (f) {
        return { el: f, on: -1, kids: all(f, '[data-film-at]').filter(function (k) { return k.parentElement.closest('[data-film-flip]') === f; })
          .map(function (k) { return { el: k, at: num(k.getAttribute('data-film-at'), 0) }; }) };
      });
      s.clips = all(el, 'canvas[data-film-clip]').map(function (c) {
        var js = c.nextElementSibling, sets = {};
        if (js && js.hasAttribute('data-film-frames')) {
          try { sets = JSON.parse(js.textContent); } catch (e) { console.warn('hf-film: frames are not JSON in shot', i, e); }
        }
        return { el: c, ctx: c.getContext('2d'), r: range(c.getAttribute('data-film-clip'), 1), sets: sets,
          cover: c.getAttribute('data-film-fit') === 'cover', urls: null, F: 0, st: null, bmp: [], n: 0, gen: 0,
          drawn: -1, f: 0, q: 0, want: [], sparse: [], shot: s };
      });
      return s;
    });

    // ---- clips ----------------------------------------------------------------------------------
    function release(c) {
      c.bmp.forEach(function (b) { if (b && b.close) b.close(); });
      c.bmp = new Array(c.F);
      if (c.st) c.st.fill(0);
      c.n = 0;
      c.gen++;
    }

    function sizeClip(c) {
      var dpr = Math.min(devicePixelRatio || 1, 2);
      var cw = Math.round(c.el.clientWidth * dpr), ch = Math.round(c.el.clientHeight * dpr);
      if (cw !== c.el.width || ch !== c.el.height) { c.el.width = cw; c.el.height = ch; c.drawn = -1; }
      var urls = (orient === 'p' && c.sets.p && c.sets.p.length ? c.sets.p : c.sets.l) || [];
      if (urls === c.urls) return;
      release(c);
      c.urls = urls;
      c.F = urls.length;
      c.st = new Uint8Array(c.F);
      c.bmp = new Array(c.F);
      c.drawn = -1;
      c.sparse = [];
      var step = Math.max(1, Math.ceil((c.F - 1) / SPARSE));
      for (var i = 0; i < c.F; i += step) c.sparse.push(i);
      if (c.F && c.sparse[c.sparse.length - 1] !== c.F - 1) c.sparse.push(c.F - 1);
    }

    // The target first, then the sparse set nearest out, then (not skimming, and the shot on screen
    // or next to it) the frames round the target, ahead of it first. Never more than the budget.
    function want(c, dense) {
      var list = [c.f], seen = {};
      seen[c.f] = 1;
      function add(j) { if (j >= 0 && j < c.F && !seen[j] && list.length < MAX_BMP) { seen[j] = 1; list.push(j); } }
      c.sparse.slice().sort(function (a, b) { return Math.abs(a - c.f) - Math.abs(b - c.f); }).forEach(add);
      if (dense && !skim) for (var d = 1; d < c.F && list.length < MAX_BMP; d++) { add(c.f + d * dirn); add(c.f - d * dirn); }
      c.want = list;
    }

    // Over budget, drop the decoded frames farthest from the playhead that are not wanted.
    function trim(c) {
      if (c.n <= MAX_BMP) return;
      var keep = {};
      c.want.forEach(function (j) { keep[j] = 1; });
      var held = [];
      c.bmp.forEach(function (b, j) { if (b && !keep[j]) held.push(j); });
      held.sort(function (a, b) { return Math.abs(b - c.f) - Math.abs(a - c.f); });
      while (c.n > MAX_BMP && held.length) {
        var j = held.shift(), b = c.bmp[j];
        if (b.close) b.close();
        c.bmp[j] = null;
        c.st[j] = 0;
        c.n--;
      }
    }

    function fetchFrame(c, i) {
      var gen = c.gen, img = new Image();
      c.st[i] = 1;
      loads++;
      function done(bmp) {
        loads--;
        if (dead || gen !== c.gen) { if (bmp && bmp.close) bmp.close(); } else if (!bmp) c.st[i] = 3;
        else { c.bmp[i] = bmp; c.st[i] = 2; c.n++; trim(c); }
        schedule();
      }
      img.decoding = 'async';
      img.onerror = function () { done(null); };
      // Decoded before it is drawn, so a draw during scroll never decodes on the main thread. A
      // bitmap is held at the size it draws, which keeps 24 of them affordable on a phone.
      img.onload = function () {
        img.decode().then(function () {
          if (!window.createImageBitmap) return done(img);
          var k = Math.min(c.el.width / img.naturalWidth, c.el.height / img.naturalHeight);
          if (c.cover) k = Math.max(c.el.width / img.naturalWidth, c.el.height / img.naturalHeight);
          var o = k > 0 && k < 1 ? { resizeWidth: Math.round(img.naturalWidth * k), resizeHeight: Math.round(img.naturalHeight * k), resizeQuality: 'medium' } : undefined;
          (o ? createImageBitmap(img, o).catch(function () { return createImageBitmap(img); }) : createImageBitmap(img))
            .then(done, function () { done(img); });
        }, function () { done(img); });
      };
      img.src = c.urls[i];
    }

    function pump() {
      for (var k = 0; k < queue.length && loads < MAX_LOADS; k++) {
        var c = queue[k];
        for (var j = 0; j < c.want.length && loads < MAX_LOADS; j++) if (!c.st[c.want[j]]) fetchFrame(c, c.want[j]);
      }
    }

    function nearest(c, i) {
      for (var d = 0; d < c.F; d++) {
        if (i - d >= 0 && c.st[i - d] === 2) return i - d;
        if (i + d < c.F && c.st[i + d] === 2) return i + d;
      }
      return -1;
    }

    // Never clears without drawing.
    function drawClip(c) {
      var i = nearest(c, c.f);
      if (i < 0 || i === c.drawn || !c.el.width) return;
      var b = c.bmp[i], cw = c.el.width, ch = c.el.height, bw = b.naturalWidth || b.width, bh = b.naturalHeight || b.height;
      var k = (c.cover ? Math.max : Math.min)(cw / bw, ch / bh), w = bw * k, h = bh * k;
      c.ctx.clearRect(0, 0, cw, ch);
      c.ctx.drawImage(b, (cw - w) / 2, (ch - h) / 2, w, h);
      c.drawn = i;
      c.el.setAttribute('data-film-frame', i);
    }

    // ---- loading --------------------------------------------------------------------------------
    function promote(s) {
      s.promoted = true;
      var decodes = s.imgs.map(function (el) {
        var ss = el.getAttribute('data-film-srcset'), src = el.getAttribute('data-film-src');
        if (ss) el.srcset = ss;
        if (src) el.src = src;
        return el.decode ? el.decode().catch(function () {}) : null;
      });
      // Images without width and height change their layer's box once they arrive.
      Promise.all(decodes).then(scheduleLayout);
    }

    function load() {
      queue = [];
      if (!pageLoaded || !near || cur < 0) return;
      var a = engaged ? cur - 1 : cur, b = engaged ? cur + 2 : cur;
      shots.forEach(function (s, i) {
        var inside = i >= a && i <= b;
        if (inside && !s.promoted) promote(s);
        s.clips.forEach(function (c) {
          if (!c.F) return;
          if (!inside) { if (c.n) release(c); return; }
          if (!s.on) c.f = t < s.S ? 0 : c.F - 1;
          want(c, Math.abs(i - cur) <= 1);
          queue.push(c);
        });
      });
      queue.sort(function (x, y) { return Math.abs(x.shot.i - cur) - Math.abs(y.shot.i - cur); });
      pump();
    }

    // ---- the frame ------------------------------------------------------------------------------
    function camAt(s, p) { return s.cam ? sampleTrack(s.cam.tr[orient], p) : PROPS; }

    // A layer's matrix in its own frame (transform-origin 0 0), with the values it came from.
    function matrix(s, l, p, cam) {
      var v = sampleTrack(l.tr[orient], p), w = l.rw / 100, h = l.rh / 100, b = l.box;
      var pv = l.pivot ? [l.pivot[0] * w, l.pivot[1] * h] : [b[0] + b[2] / 2, b[1] + b[3] / 2];
      var m = tf(v.x * w, v.y * h, v.s, v.r, pv[0], pv[1]);
      if (l.cam) {
        var d = l.depth, cp = s.cam.pivot;
        m = mul(tf(cam.x * d * w, cam.y * d * h, 1 + (cam.s - 1) * d, cam.r * d, cp[0] * w, cp[1] * h), m);
      }
      m[4] += m[0] * b[0] + m[2] * b[1] - b[0];
      m[5] += m[1] * b[0] + m[3] * b[1] - b[1];
      v.m = m.map(r4);
      return v;
    }

    function render(s) {
      if (s.O > 0 && s.fade) {
        var op = String(r4(EASE.inOut(clamp01((t - s.S) / s.O))));
        if (op !== s.op) s.el.style.opacity = s.op = op;
      } else if (s.op !== '1') s.el.style.opacity = s.op = '1';
      var p = clamp01((t - s.S) / s.L);
      if (p !== s.p) {
        s.p = p;
        s.el.style.setProperty('--film-p', p.toFixed(4));
        var cam = camAt(s, p);
        if (s.cam && String(r4(cam.o)) !== s.cam.o) s.cam.el.style.opacity = s.cam.o = String(r4(cam.o));
        s.layers.forEach(function (l) {
          var v = matrix(s, l, p, cam), css = 'matrix(' + v.m.join(',') + ')', o = String(r4(v.o));
          if (css !== l.m) l.el.style.transform = l.m = css;
          if (o !== l.o) l.el.style.opacity = l.o = o;
        });
        s.cues.forEach(function (c) {
          var on = p >= c.r[0] && p < c.r[1];
          if (on !== c.on) { c.on = on; c.el.classList.toggle('is-cued', on); }
        });
        s.draws.forEach(function (d) {
          var q = seg(p, d.r), k = q.toFixed(3);
          if (k === d.v) return;
          d.v = k;
          d.el.style.setProperty('--film-draw', k);
          var left = q * d.total;
          d.segs.forEach(function (g) {
            var a = g.len ? clamp01(left / g.len) : 1;
            left -= g.len;
            g.el.style.strokeDashoffset = (g.len * (1 - a)).toFixed(2);
            g.el.style.visibility = a > 0 ? '' : 'hidden';
          });
        });
        s.tallies.forEach(function (c) {
          var q = seg(p, c.r), txt = q >= 1 ? c.text : format(c, c.from + (c.to - c.from) * EASE.out(q));
          if (txt !== c.v) c.el.textContent = c.v = txt;
        });
        s.flips.forEach(function (f) {
          var k = 0;
          f.kids.forEach(function (kid, j) { if (p >= kid.at) k = j; });
          if (k === f.on) return;
          if (f.kids[f.on]) f.kids[f.on].el.removeAttribute('data-film-on');
          f.on = k;
          if (f.kids[k]) f.kids[k].el.setAttribute('data-film-on', '');
          f.el.style.setProperty('--film-flip', k);
        });
      }
      s.clips.forEach(function (c) {
        if (!c.F) return;
        c.q = seg(p, c.r);
        c.f = Math.round(c.q * (c.F - 1));
        drawClip(c);
      });
    }

    function layout() {
      layoutRaf = 0;
      if (dead) return;
      W = stage.clientWidth;
      H = stage.clientHeight || innerHeight;
      top = parseFloat(getComputedStyle(stage).top) || 0;
      var o = W < H ? 'p' : 'l', A = o === 'p' ? 9 / 16 : 16 / 9;
      if (o !== orient) {
        orient = o;
        root.setAttribute('data-film-orient', o);
      }
      aw = W / H > A ? W : H * A;
      ah = aw / A;
      root.style.setProperty('--film-aw', aw.toFixed(2) + 'px');
      root.style.setProperty('--film-ah', ah.toFixed(2) + 'px');

      shots.forEach(function (s, i) {
        var prev = shots[i - 1];
        s.L = Math.max(0.05, o === 'p' && !isNaN(s.lenP) ? s.lenP : s.lenL);
        s.O = prev ? Math.max(0, Math.min(s.over, s.L, prev.L)) : 0;
        s.S = prev ? prev.S + prev.L - s.O : 0;
        s.layers.forEach(function (l) { l.el.style.transform = 'none'; l.el.style.transformOrigin = '0 0'; l.m = ''; });
      });
      T = shots[shots.length - 1].S + shots[shots.length - 1].L;
      trackEl.style.height = Math.round(T * H + H) + 'px';

      // One read pass with every layer untransformed, then the writes.
      shots.forEach(function (s) {
        s.layers.forEach(function (l) {
          var r = l.el.getBoundingClientRect(), rr = l.ref.getBoundingClientRect();
          l.box = [r.left - rr.left, r.top - rr.top, r.width, r.height];
          l.rw = rr.width;
          l.rh = rr.height;
        });
        s.draws.forEach(function (d) {
          d.total = 0;
          d.segs.forEach(function (g) { g.len = g.el.getTotalLength ? g.el.getTotalLength() : 0; d.total += g.len; });
        });
        s.tallies.forEach(function (c) {
          c.el.textContent = c.text;
          c.el.style.minWidth = '';
          c.w = c.el.getBoundingClientRect().width;
        });
      });
      shots.forEach(function (s) {
        s.draws.forEach(function (d) {
          d.v = '';
          d.segs.forEach(function (g) { g.el.style.strokeDasharray = g.len.toFixed(2) + ' ' + g.len.toFixed(2); });
        });
        s.tallies.forEach(function (c) { c.el.style.minWidth = Math.ceil(c.w) + 'px'; c.v = null; });
        s.clips.forEach(sizeClip);
        s.p = -1;
      });
      update(performance.now());
    }

    function update(now) {
      raf = 0;
      if (dead) return;
      var raw = (top - trackEl.getBoundingClientRect().top) / H;
      var nt = raw < 0 ? 0 : raw > T ? T : raw;
      if (nt !== lastT) {
        if (lastT >= 0 && now - lastNow < 100) {
          dirn = nt > lastT ? 1 : -1;
          if (Math.abs(nt - lastT) / Math.max(now - lastNow, 1) * 1000 > SKIM) skim = true;
        }
        clearTimeout(settle);
        settle = setTimeout(function () { if (skim) { skim = false; schedule(); } }, 160);
        lastT = nt;
        lastNow = now;
      }
      t = nt;

      var c = 0;
      shots.forEach(function (s, i) {
        if (s.S <= t) c = i;
        var on = t >= s.S && t <= s.S + s.L;
        if (on !== s.on) {
          s.on = on;
          s.el.classList.toggle('is-on', on);
        }
        if (on) render(s);
        // A scene off screen rests at its start or its end, whichever side of it the reader is,
        // so the film's state depends only on where it is, not on the way it got there.
        else if (s.p !== (t < s.S ? 0 : 1) || s.op) { render(s); s.el.style.opacity = s.op = ''; }
      });
      if (c !== cur) {
        cur = c;
        root.setAttribute('data-film-current', shots[c].name);
      }
      load();
      if (dbg) readout();
    }

    function readout() {
      var s = shots[cur], n = 0;
      shots.forEach(function (x) { x.clips.forEach(function (k) { n += k.n; }); });
      dbg.textContent = 't ' + t.toFixed(3) + ' / ' + T.toFixed(2) + '  ' + orient + (skim ? '  skim' : '') +
        '\n' + s.name + '  p ' + clamp01((t - s.S) / s.L).toFixed(3) + '\nloads ' + loads + '  frames held ' + n;
    }

    // ---- control --------------------------------------------------------------------------------
    function shotOf(x) {
      if (typeof x === 'number') return shots[x];
      if (typeof x === 'string') return shots.filter(function (s) { return s.name === x; })[0];
      return shots.filter(function (s) { return s.el === x || s.el.contains(x) || (x.contains && x.contains(s.el)); })[0];
    }

    function seek(to, p) {
      var tt = to;
      if (typeof to !== 'number' || p != null) {
        var s = shotOf(to);
        if (!s) return null;
        tt = s.S + (p == null ? s.hold : p) * s.L;
      }
      tt = Math.max(0, Math.min(T, tt));
      engaged = true;
      var y = trackEl.getBoundingClientRect().top + scrollY - top + tt * H;
      instant(function () { scrollTo(0, y); });
      update(performance.now());
      return t;
    }

    function state() {
      return {
        t: t, T: T, W: W, H: H, top: top, orient: orient, art: [aw, ah], current: shots[cur] && shots[cur].name,
        skim: skim, engaged: engaged, near: near, loaded: pageLoaded, loads: loads, instances: live.length,
        shots: shots.map(function (s) {
          return {
            name: s.name, S: s.S, L: s.L, O: s.O, hold: s.hold, p: clamp01((t - s.S) / s.L), on: s.on, opacity: s.el.style.opacity,
            promoted: s.promoted, ready: s.imgs.every(function (e) { return e.tagName !== 'IMG' || (e.complete && e.naturalWidth > 0); }),
            cued: s.cues.filter(function (c) { return c.on; }).length, flips: s.flips.map(function (f) { return f.on; }),
            clips: s.clips.map(function (c) {
              var b = 0;
              c.bmp.forEach(function (x) { if (x) b += (x.naturalWidth || x.width) * (x.naturalHeight || x.height) * 4; });
              return { frames: c.F, target: c.f, drawn: c.drawn, held: c.n, bytes: b, q: c.q };
            })
          };
        })
      };
    }

    function sample(el, p) {
      for (var i = 0; i < shots.length; i++) {
        var s = shots[i], pp = p == null ? clamp01((t - s.S) / s.L) : p, cam = camAt(s, pp), k;
        if (s.cam && s.cam.el === el) { k = {}; for (var n in PROPS) k[n] = cam[n]; k.p = pp; return k; }
        for (var j = 0; j < s.layers.length; j++) {
          var l = s.layers[j];
          if (l.el === el) {
            k = matrix(s, l, pp, cam);
            k.p = pp; k.box = l.box.slice(); k.ref = [l.rw, l.rh]; k.depth = l.depth; k.cam = l.cam && cam;
            k.pivot = l.pivot; k.camPivot = s.cam && s.cam.pivot;
            return k;
          }
        }
      }
      return null;
    }

    function destroy() {
      dead = true;
      ac.abort();
      io.disconnect();
      ro.disconnect();
      cancelAnimationFrame(raf);
      cancelAnimationFrame(layoutRaf);
      clearTimeout(settle);
      root.classList.remove('is-film-live', 'is-film-loaded', 'is-film-debug');
      root.removeAttribute('data-film-orient');
      root.removeAttribute('data-film-current');
      root.style.removeProperty('--film-aw');
      root.style.removeProperty('--film-ah');
      trackEl.style.height = '';
      shots.forEach(function (s) {
        s.el.classList.remove('is-on');
        s.el.style.opacity = '';
        s.el.style.removeProperty('--film-p');
        s.el.style.removeProperty('--film-ground');
        if (s.cam) s.cam.el.style.opacity = '';
        s.layers.forEach(function (l) { l.el.style.transform = l.el.style.opacity = l.el.style.transformOrigin = ''; });
        s.cues.forEach(function (c) { c.el.classList.remove('is-cued'); });
        s.draws.forEach(function (d) {
          d.el.style.removeProperty('--film-draw');
          d.segs.forEach(function (g) { g.el.style.strokeDasharray = g.el.style.strokeDashoffset = g.el.style.visibility = ''; });
        });
        s.tallies.forEach(function (c) { c.el.textContent = c.text; c.el.style.minWidth = ''; });
        s.flips.forEach(function (f) {
          f.el.style.removeProperty('--film-flip');
          f.kids.forEach(function (k) { k.el.removeAttribute('data-film-on'); });
        });
        s.clips.forEach(function (c) { release(c); c.el.removeAttribute('data-film-frame'); });
      });
      if (dbg) dbg.remove();
      live.splice(live.indexOf(inst), 1);
      delete root.__hfFilm;
    }

    var io = new IntersectionObserver(function (entries) {
      near = entries[entries.length - 1].isIntersecting;
      schedule();
    }, { rootMargin: '150% 0px' });
    var ro = new ResizeObserver(scheduleLayout);

    addEventListener('scroll', function () { engaged = true; if (near) schedule(); }, opt);
    // A keyboard reader reaching a link in a scene that is not showing is taken to that scene.
    root.addEventListener('focusin', function (e) {
      var s = shotOf(e.target);
      if (s && !s.on) seek(s.i, s.hold);
    }, { signal: ac.signal });
    if (document.fonts) document.fonts.ready.then(scheduleLayout);

    var inst = {
      root: root, track: trackEl, destroy: destroy, seek: seek, state: state, sample: sample,
      pick: function (el) { var s = shotOf(el); if (s) seek(s.i, s.hold); },
      loaded: function () { root.classList.add('is-film-loaded'); schedule(); }
    };
    root.__hfFilm = inst;
    live.push(inst);
    root.classList.add('is-film-live');
    if (pageLoaded) root.classList.add('is-film-loaded');
    shots.forEach(function (s) {
      var g = s.el.getAttribute('data-film-ground');
      if (g) s.el.style.setProperty('--film-ground', g);
    });
    if (debug) {
      dbg = document.createElement('pre');
      dbg.className = 'hf-film-debug';
      document.body.appendChild(dbg);
      root.classList.add('is-film-debug');
    }
    layout();
    io.observe(root);
    ro.observe(stage);
  }

  function scan(scope) {
    if (reduceMQ.matches) return;
    var roots = all(scope, '[data-film]');
    if (scope.matches && scope.matches('[data-film]')) roots.unshift(scope);
    roots.forEach(mount);
  }
  function drop(scope) {
    live.slice().forEach(function (f) { if (scope === f.root || scope.contains(f.root)) f.destroy(); });
  }

  // Same page links that would scroll across more than a screen of film jump there instantly.
  function jump(e) {
    if (e.defaultPrevented || e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || !live.length) return;
    var a = e.target.closest && e.target.closest('a[href*="#"]');
    if (!a) return;
    var u = new URL(a.href, location.href);
    if (u.pathname !== location.pathname || u.search !== location.search || u.hash.length < 2) return;
    var dest = document.getElementById(decodeURIComponent(u.hash.slice(1)));
    if (!dest) return;
    var y0 = scrollY, y1 = dest.getBoundingClientRect().top + y0;
    var cross = live.some(function (f) {
      var r = f.track.getBoundingClientRect(), s = f.state();
      var a0 = r.top + y0 - s.top, b0 = a0 + s.T * s.H;
      return Math.min(Math.max(y0, y1), b0) - Math.max(Math.min(y0, y1), a0) > s.H;
    });
    if (!cross) return;
    e.preventDefault();
    instant(function () { dest.scrollIntoView({ block: 'start' }); });
    if (location.hash !== u.hash) history.pushState(null, '', u.hash);
    if (!dest.matches('a[href],button,input,select,textarea,[tabindex]')) dest.setAttribute('tabindex', '-1');
    dest.focus({ preventScroll: true });
  }

  reduceMQ.addEventListener('change', function () { if (reduceMQ.matches) drop(document); else scan(document); });
  // The theme editor swaps a section's HTML without reloading the page.
  document.addEventListener('shopify:section:load', function (e) { scan(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { drop(e.target); });
  document.addEventListener('shopify:block:select', function (e) {
    live.forEach(function (f) {
      if (!f.root.contains(e.target)) return;
      f.pick(e.target);
      // The editor scrolls to the block itself once it has sent the event.
      setTimeout(function () { f.pick(e.target); }, 400);
    });
  });
  document.addEventListener('click', jump);
  addEventListener('load', function () {
    pageLoaded = true;
    live.forEach(function (f) { f.loaded(); });
  });

  function first() { return live[0]; }
  window.HfFilm = {
    scan: scan,
    drop: drop,
    seek: function (to, p) { return first() ? first().seek(to, p) : null; },
    state: function () { return first() ? first().state() : null; },
    sample: function (what, p) {
      if (what && !what.nodeType && typeof what !== 'string') return sampleTrack(track(what), p || 0);
      var el = typeof what === 'string' ? document.querySelector(what) : what;
      for (var i = 0; i < live.length; i++) { var v = live[i].sample(el, p); if (v) return v; }
      return null;
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { scan(document); });
  else scan(document);
})();
